import * as turf from "@turf/turf";

export class UtilsGeo {

    constructor() {}

    public toGoogleLatLng(coords: {lat: any, lng?: any}): google.maps.LatLng {
        const type = typeof coords.lat;
        if (type === 'object' && coords.lat.geometry) {
            if (Number.isInteger(coords.lng)) return new google.maps.LatLng(coords.lat.geometry.coordinates[coords.lng][1], coords.lat.geometry.coordinates[coords.lng][0]);
            return new google.maps.LatLng(coords.lat.geometry.coordinates[1], coords.lat.geometry.coordinates[0]);
        } else if (type === 'object' && coords.lat.latitude !== undefined && coords.lat.longitude !== undefined) {
            return new google.maps.LatLng(coords.lat.latitude, coords.lat.longitude);
        } else if (Array.isArray(coords.lat) && coords.lat.length === 2) {
            return new google.maps.LatLng(coords.lat[1], coords.lat[0]);
        } else if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
            return new google.maps.LatLng(coords.lat, coords.lng);
        } else {
            throw new Error('Unknown parameter type to convert into google lat lng: ' + coords.lat + ' | ' + coords.lng);
        }
    }

    public toGeoJsonCoordinates(p1: any, p2?: number): [number, number] {
        if (typeof p1 === 'object' && p1.geometry) {
            return [this.getLng(p1, p2), this.getLat(p1, p2)];
        } else if (typeof p1 === 'number' && typeof p2 === 'number') {
            return [p2, p1]; // GeoJSON uses [lng, lat]
        } else {
            throw new Error('Unknown parameter type to convert into geojson coordinates.');
        }
    }

    public setGeoJsonCoordinates(geoJsonObj: any, lat: number, lng: number, index?: number) {
        if (Number.isInteger(index)) {
            geoJsonObj.geometry.coordinates[index as number][1] = lat;
            geoJsonObj.geometry.coordinates[index as number][0] = lng;
        } else {
            geoJsonObj.geometry.coordinates[1] = lat;
            geoJsonObj.geometry.coordinates[0] = lng;
        }
    }

    public getLat(obj: any, index?: number): number {
        if (typeof obj === 'object' && obj.geometry) return this.getGeoJsonCoordinate(obj, 1, index);
        throw new Error('Unknown parameter type to convert into geojson lat.');
    }

    public getLng(obj: any, index?: number): number {
        if (typeof obj === 'object' && obj.geometry) return this.getGeoJsonCoordinate(obj, 0, index);
        throw new Error('Unknown parameter type to convert into geojson lng.');
    }

    private getGeoJsonCoordinate(obj: any, latLngIndex: number, coordIndex?: number): number {
        if (!obj.geometry.coordinates) return 0;

        if (typeof obj.geometry.coordinates[0] === 'number') return obj.geometry.coordinates[latLngIndex];
        if (Array.isArray(obj.geometry.coordinates[0]) && Number.isInteger(coordIndex)) {
            return obj.geometry.coordinates[coordIndex as number][latLngIndex];
        }
        throw new Error('Not supported or invalid geojson object.');
    }

    public syncGeometry(segment: any) {
        if (!segment?.properties?.polyline) return;
        const updatedGeometry: [number, number][] = [];
        segment.properties.polyline.getPath().forEach((latLng: google.maps.LatLng) => {
            updatedGeometry.push([latLng.lng(), latLng.lat()]);
        });
        segment.geometry.coordinates = updatedGeometry;
    }

    public logGeoJsonFeature(f: any) {
        if (!f) return;
        const clone = structuredClone(f); // reemplazo de $.extend(true, {}, f)
        if (clone.properties) clone.properties.polyline = null;
    }

    public calculateSegmentDistance(segment: any) {
        const line = turf.lineString(segment.geometry.coordinates);
        return turf.length(line, { units: 'meters' });
    }

    public calculateSegmentDuration(meters: number, drivingSpeed: number) {
        const travelTimeInSec = (meters * 3.6) / drivingSpeed;
        return Math.round(travelTimeInSec);
    }

    public calculateDrivingSpeed(distance: number, duration: number) {
        return Math.round((distance / duration) * 3600);
    }

    public calculateDistanceBetweenPoints(p1: {lat: number, lng: number}, p2: {lat: number, lng: number}): number {
        const from = turf.point([p1.lng, p1.lat]);
        const to = turf.point([p2.lng, p2.lat]);
        return turf.distance(from, to, { units: 'meters' });
    }

    public getClosingThreshold(zoom: number, lat: number, pixelRadius: number = 20): number {
        const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
        return metersPerPixel * pixelRadius;
    }

    public getOutlineTopRightPoint(coordinates: number[][][]): {lat: number, lng: number} {
        const ring = coordinates[0];
        const bbox = turf.bbox(turf.polygon(coordinates));
        const northeast = turf.point([bbox[2], bbox[3]]);
        let closest = ring[0];
        let minDist = Infinity;
        for (const coord of ring) {
            const dist = turf.distance(turf.point(coord), northeast, { units: 'meters' });
            if (dist < minDist) {
                minDist = dist;
                closest = coord;
            }
        }
        return { lat: closest[1], lng: closest[0] };
    }
}

// instancia global como antes
export const utilsGeo = new UtilsGeo();
